/**
 * StepContainer — wraps a step body with hero icon, headline, sub, and the
 * forward/backward slide+fade animation per visual spec (ease-out-quint).
 */

import type { ReactNode } from "react";
import { motion } from "framer-motion";
import type { Icon as LucideIcon } from "@phosphor-icons/react";
import { Sparkle as Sparkles } from "@phosphor-icons/react";
import { useOnboardingStore } from "../store/onboardingStore";

export interface StepContainerProps {
  icon: LucideIcon;
  headline: string;
  sub?: string;
  importanceNote?: string;
  children: ReactNode;
}

const EASE = [0.22, 1, 0.36, 1] as const;

export function StepContainer({
  icon: Icon,
  headline,
  sub,
  importanceNote,
  children,
}: StepContainerProps) {
  const direction = useOnboardingStore((s) => s.direction);
  const fromX = direction === "forward" ? 24 : -24;

  return (
    <motion.div
      initial={{ x: fromX, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -fromX, opacity: 0 }}
      transition={{ duration: 0.35, ease: EASE }}
      className="flex flex-1 flex-col gap-6"
    >
      <div className="flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary md:size-14">
        <Icon className="size-7" strokeWidth={1.75} />
      </div>

      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight md:text-4xl">
          {headline}
        </h1>
        {sub ? (
          <p className="text-base text-muted-foreground">{sub}</p>
        ) : null}
      </div>

      <div className="flex-1">{children}</div>

      {importanceNote ? (
        <p className="flex items-center gap-1.5 text-xs italic text-muted-foreground">
          <Sparkles className="size-3.5" strokeWidth={1.75} />
          {importanceNote}
        </p>
      ) : null}
    </motion.div>
  );
}
